import type { RouteObject } from "react-router";
import type { HighlighterCore } from "shiki";
import { configuredApiKeysPlugin } from "virtual:zudoku-api-keys-plugin";
import {
  configuredApiCatalogPlugins,
  configuredApiPlugins,
} from "virtual:zudoku-api-plugins";
import { configuredAuthProvider } from "virtual:zudoku-auth";
import { configuredCustomPagesPlugin } from "virtual:zudoku-custom-pages-plugin";
import { configuredDocsPlugin } from "virtual:zudoku-docs-plugin";
import {
  configuredHeaderNavigation,
  configuredNavigation,
  configuredNavigationRules,
} from "virtual:zudoku-navigation";
import { configuredSearchPlugin } from "virtual:zudoku-search-plugin";
import "virtual:zudoku-theme.css";
import { Zudoku } from "zudoku/components";
import { Outlet } from "zudoku/router";
import type { ZudokuConfig } from "../config/config.js";
import { authState } from "../lib/authentication/state.js";
import { BuildCheck } from "../lib/components/BuildCheck.js";
import { Meta } from "../lib/components/Meta.js";
import "./main.css";
import { StatusPage } from "../lib/components/StatusPage.js";
import "./polyfills.js";
import { isNavigationPlugin } from "../lib/core/plugins.js";
import { RouteGuard } from "../lib/core/RouteGuard.js";
import type { ZudokuContextOptions } from "../lib/core/ZudokuContext.js";
import { RouterError } from "../lib/errors/RouterError.js";
import { ZuploEnv } from "./env.js";
import { processRoutes } from "./processRoutes.js";
import { createRedirectRoutes } from "./utils/createRedirectRoutes.js";
import {
  warnInlineProtectedRoutes,
  wrapProtectedRoutes,
} from "./wrapProtectedRoutes.js";

export const shikiReady: Promise<HighlighterCore> =
  import("../lib/shiki.js").then(async ({ highlighterPromise }) => {
    const highlighter = await highlighterPromise;
    const { registerShiki } = await import("virtual:zudoku-shiki-register");
    await registerShiki(highlighter);
    return highlighter;
  });

export const convertZudokuConfigToOptions = (
  config: ZudokuConfig,
): ZudokuContextOptions => {
  return {
    basePath: config.basePath,
    canonicalUrlOrigin: config.canonicalUrlOrigin,
    aiAssistants: config.aiAssistants,
    protectedRoutes: config.protectedRoutes,
    site: {
      ...config.site,
      showPoweredBy:
        ZuploEnv.buildConfig?.entitlements.devPortalZuploBranding ??
        config.site?.showPoweredBy,
      logo: config.site?.logo,
    },
    slots: config.slots,
    metadata: {
      favicon: "https://cdn.zudoku.dev/logos/favicon.svg",
      title: "%s - Zudoku",
      ...config.metadata,
    },
    header: {
      navigation: configuredHeaderNavigation,
      placements: config.header?.placements,
      themeSwitcher: config.header?.themeSwitcher,
    },
    navigation: configuredNavigation,
    navigationRules: configuredNavigationRules,
    mdx: config.mdx,
    plugins: [
      ...(configuredAuthProvider ? [configuredAuthProvider] : []),
      ...(configuredDocsPlugin ? [configuredDocsPlugin] : []),
      ...configuredApiPlugins,
      ...(configuredSearchPlugin ? [configuredSearchPlugin] : []),
      ...(configuredApiKeysPlugin ? [configuredApiKeysPlugin] : []),
      ...(configuredCustomPagesPlugin ? [configuredCustomPagesPlugin] : []),
      ...configuredApiCatalogPlugins,
      ...(config.plugins ?? []),
    ],
    syntaxHighlighting: {
      highlighterPromise: shikiReady,
      themes: config.syntaxHighlighting?.themes,
    },
  };
};

export const getRoutesByOptions = (
  options: ZudokuContextOptions,
  enableStatusPages = true,
) => {
  const allPlugins = [
    ...(options.plugins ?? []),
    ...(options.authentication ? [options.authentication] : []),
  ];

  const routes = allPlugins
    .flatMap((plugin) => (isNavigationPlugin(plugin) ? plugin.getRoutes() : []))
    .concat(
      enableStatusPages
        ? [400, 404, 500].map((statusCode) => ({
            path: `/${statusCode}`,
            element: <StatusPage statusCode={statusCode} />,
          }))
        : [],
    )
    .concat([{ path: "*", element: <StatusPage statusCode={404} /> }])
    .map((route) => ({
      ...route,
      errorElement: <RouterError className="w-full m-0" />,
    }));

  // @TODO Detect conflicts in routes and log warning

  return routes;
};

export const getRoutesByConfig = (config: ZudokuConfig): RouteObject[] => {
  const options = convertZudokuConfigToOptions(config);
  const routes = getRoutesByOptions(
    options,
    import.meta.env.IS_ZUPLO || config.enableStatusPages,
  );

  if (import.meta.env.DEV && typeof window !== "undefined") {
    warnInlineProtectedRoutes(routes, config.protectedRoutes, config.basePath);
  }

  return [
    ...createRedirectRoutes(config.redirects),
    {
      element: (
        <Zudoku {...options} env={import.meta.env}>
          <BuildCheck
            buildId={import.meta.env.ZUPLO_BUILD_ID}
            environmentType={import.meta.env.ZUPLO_ENVIRONMENT_TYPE}
          />
          <Outlet />
        </Zudoku>
      ),
      children: [
        {
          element: (
            <Meta>
              <RouteGuard />
            </Meta>
          ),
          errorElement: <RouterError />,
          // Chunk-gate protected routes only in SSR; SSG has no 401 to avoid.
          children:
            typeof window === "undefined" || !import.meta.env.ZUDOKU_HAS_SERVER
              ? processRoutes(routes)
              : wrapProtectedRoutes(
                  processRoutes(routes),
                  config.protectedRoutes,
                  authState.getState().isAuthenticated,
                  config.basePath,
                ),
        },
      ],
    },
  ];
};
